// =============================================================================
// API CLIENT
// =============================================================================
// Axios wrapper for the CCN Sanluri backend. All calls travel through the same
// `api` instance so they inherit:
//   - baseURL from REACT_APP_BACKEND_URL (never hardcoded)
//   - withCredentials=true (needed for the HttpOnly `ccn_session` auth cookie)
//   - a lightweight response interceptor that clears the cached user on 401
//
// Three logical namespaces exist today:
//   • publicApi     → open endpoints (categories, businesses, spotlight)
//   • adminApi      → admin-only endpoints (dashboard CRUD, membership admin)
//   • membershipApi → public form submission for "Diventa socio"
//
// FUTURE (Area Riservata):
// When the members-only area is built, add a fourth namespace `memberApi`
// following the same pattern. Do NOT nest it under adminApi — members are a
// distinct role and must not inherit admin behavior.
// =============================================================================

import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BASE}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

export const publicApi = {
  health: () => api.get("/health"),
  listCategories: () => api.get("/categories"),
  getCategory: (slug) => api.get(`/categories/${slug}`),
  listSubcategories: (categorySlug) =>
    api.get("/subcategories", { params: categorySlug ? { category: categorySlug } : {} }),
  getSubcategory: (categorySlug, subSlug) =>
    api.get(`/categories/${categorySlug}/subcategories/${subSlug}`),
  listBusinesses: (params = {}) => api.get("/businesses", { params }),
  spotlight: (limit = 6, exclude = []) => api.get("/businesses/spotlight", {
    params: { limit, ...(exclude.length ? { exclude: exclude.join(",") } : {}) },
  }),
  getBusiness: (slug) => api.get(`/businesses/${slug}`),
  // Events (public)
  listEvents: (params = {}) => api.get("/events", { params }),
  getEvent: (slug) => api.get(`/events/${slug}`),
  trackEvent: (slug, kind) => api.post(`/events/${slug}/track`, { kind }),
  getSiteConfig: () => api.get("/site-config"),
};

export const authApi = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  logout: () => api.post("/auth/logout"),
  me: () => api.get("/auth/me"),
  requestPasswordReset: (email, redirect_to) =>
    api.post("/auth/reset-password", { email, redirect_to }),
  mfa: {
    status: () => api.get("/auth/mfa/status"),
    enroll: () => api.post("/auth/mfa/enroll"),
    challenge: (factor_id) => api.post("/auth/mfa/challenge", { factor_id }),
    verify: (factor_id, challenge_id, code) =>
      api.post("/auth/mfa/verify", { factor_id, challenge_id, code }),
  },
};

export const adminApi = {
  listBusinesses: () => api.get("/admin/businesses"),
  getBusiness: (id) => api.get(`/admin/businesses/${id}`),
  createBusiness: (payload) => api.post("/admin/businesses", payload),
  updateBusiness: (id, payload) => api.patch(`/admin/businesses/${id}`, payload),
  deleteBusiness: (id) => api.delete(`/admin/businesses/${id}`),
  geocode: (address) => api.post("/admin/geocode", { address }),
  createCategory: (payload) => api.post("/admin/categories", payload),
  updateCategory: (id, payload) => api.patch(`/admin/categories/${id}`, payload),
  createSubcategory: (payload) => api.post("/admin/subcategories", payload),
  updateSubcategory: (id, payload) => api.patch(`/admin/subcategories/${id}`, payload),
  listMembership: (status) => api.get("/admin/membership-requests", { params: status ? { status } : {} }),
  getMembership: (id) => api.get(`/admin/membership-requests/${id}`),
  updateMembership: (id, payload) => api.patch(`/admin/membership-requests/${id}`, payload),
  convertMembership: (id) => api.post(`/admin/membership-requests/${id}/convert`),
  getSiteSettings: () => api.get("/admin/site-settings"),
  updateSiteSettings: (payload) => api.patch("/admin/site-settings", payload),
  getSystemInfo: () => api.get("/admin/system-info"),
  // Events (admin CRUD)
  listEvents: () => api.get("/admin/events"),
  listEventsTrash: () => api.get("/admin/events/trash"),
  getEvent: (id) => api.get(`/admin/events/${id}`),
  createEvent: (payload) => api.post("/admin/events", payload),
  updateEvent: (id, payload) => api.patch(`/admin/events/${id}`, payload),
  deleteEvent: (id) => api.delete(`/admin/events/${id}`),
  restoreEvent: (id) => api.post(`/admin/events/${id}/restore`),
  importPreview: (file, on_conflict = "skip") => {
    const form = new FormData();
    form.append("file", file); form.append("mode", "preview"); form.append("on_conflict", on_conflict);
    return api.post("/admin/import/businesses", form, { headers: { "Content-Type": "multipart/form-data" } });
  },
  importCommit: (file, on_conflict = "skip") => {
    const form = new FormData();
    form.append("file", file); form.append("mode", "commit"); form.append("on_conflict", on_conflict);
    return api.post("/admin/import/businesses", form, { headers: { "Content-Type": "multipart/form-data" } });
  },
  exportUrl: (format = "xlsx", includeDemoBool = true) =>
    `${API}/admin/export/businesses?format=${format}&include_demo=${includeDemoBool}`,
  importTemplateUrl: (format = "xlsx") =>
    `${API}/admin/import/template?format=${format}`,
  importImagesZipPreview: (file, opts = {}) => {
    const form = new FormData();
    form.append("file", file);
    form.append("mode", "preview");
    form.append("gallery_mode", opts.gallery_mode || "append");
    form.append("replace_cover", opts.replace_cover === false ? "false" : "true");
    return api.post("/admin/import/images-zip", form, { headers: { "Content-Type": "multipart/form-data" } });
  },
  importImagesZipCommit: (file, opts = {}) => {
    const form = new FormData();
    form.append("file", file);
    form.append("mode", "commit");
    form.append("gallery_mode", opts.gallery_mode || "append");
    form.append("replace_cover", opts.replace_cover === false ? "false" : "true");
    return api.post("/admin/import/images-zip", form, { headers: { "Content-Type": "multipart/form-data" } });
  },
  importImagesZipExampleUrl: () => `${API}/admin/import/images-zip/example`,
  // Chatbot admin
  getChatbotSettings: () => api.get("/admin/chatbot/settings"),
  updateChatbotSettings: (payload) => api.patch("/admin/chatbot/settings", payload),
  getChatbotStats: () => api.get("/admin/chatbot/stats"),
  listFaqs: () => api.get("/admin/chatbot/faqs"),
  createFaq: (payload) => api.post("/admin/chatbot/faqs", payload),
  updateFaq: (id, payload) => api.patch(`/admin/chatbot/faqs/${id}`, payload),
  deleteFaq: (id) => api.delete(`/admin/chatbot/faqs/${id}`),
  // Suggestions (V8)
  listSuggestions: () => api.get("/admin/chatbot/suggestions"),
  createSuggestion: (payload) => api.post("/admin/chatbot/suggestions", payload),
  updateSuggestion: (id, payload) => api.patch(`/admin/chatbot/suggestions/${id}`, payload),
  deleteSuggestion: (id) => api.delete(`/admin/chatbot/suggestions/${id}`),
  upload: (file, folder = "misc") => {
    const form = new FormData();
    form.append("file", file);
    form.append("folder", folder);
    return api.post("/admin/upload", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  // WordPress migration (Sprint 3)
  listMigrations: () => api.get("/admin/migrations"),
  getMigration: (id) => api.get(`/admin/migrations/${id}`),
  previewMigration: (file, manifest) => {
    const form = new FormData();
    form.append("file", file);
    if (manifest) form.append("manifest", manifest);
    form.append("source_type", "wordpress-json");
    return api.post("/admin/migrations/preview", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  analyzeMigration: (id) => api.post(`/admin/migrations/${id}/analyze`),
  startMigrationImport: (id) => api.post(`/admin/migrations/${id}/import`),
  retryMigrationErrors: (id) => api.post(`/admin/migrations/${id}/retry-errors`),
  cancelMigration: (id) => api.post(`/admin/migrations/${id}/cancel`),
  resolveMigrationConflict: (jobId, itemId, resolution) =>
    api.post(`/admin/migrations/${jobId}/items/${itemId}/resolve`, null, { params: { resolution } }),
  listMigrationItems: (id, params = {}) => api.get(`/admin/migrations/${id}/items`, { params }),
  downloadMigrationReport: (id) =>
    api.get(`/admin/migrations/${id}/report`, { responseType: "blob" }),
  deleteMigration: (id) => api.delete(`/admin/migrations/${id}`),
};

export const membershipApi = {
  apply: (payload) => api.post("/membership/apply", payload),
  upload: (file, folder = "membership") => {
    const form = new FormData();
    form.append("file", file);
    form.append("folder", folder);
    return api.post("/membership/upload", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

export const chatbotApi = {
  config: () => api.get("/chatbot/config"),
  ask: (question, session_id, history) => api.post("/chatbot/ask", { question, session_id, history }),
  suggestions: () => api.get("/chatbot/suggestions"),
};

// -----------------------------------------------------------------------
// Editorial CMS (Phase 2)
// -----------------------------------------------------------------------
export const contentApi = {
  // Public (used by the frontend in Phase 3)
  getPage: (pageKey, previewToken) =>
    api.get(`/content/pages/${pageKey}`, { params: previewToken ? { preview: previewToken } : {} }),
  getUiStrings: (area) => api.get("/content/ui-strings", { params: area ? { area } : {} }),
  verifyPreview: (token) => api.get("/content/preview/verify", { params: { token } }),
};

export const contentAdminApi = {
  // Read
  dashboard: () => api.get("/admin/content/dashboard"),
  listPages: () => api.get("/admin/content/pages"),
  getPage: (pageKey) => api.get(`/admin/content/pages/${pageKey}`),
  listUiStrings: (area) => api.get("/admin/content/ui-strings", { params: area ? { area } : {} }),
  audit: (limit = 100) => api.get("/admin/content/audit", { params: { limit } }),
  health: () => api.get("/admin/content/health"),
  blockHistory: (blockId, params = {}) => api.get(`/admin/content/blocks/${blockId}/history`, { params }),
  // Write
  saveDraft: (blockId, content, ifUnmodifiedSince) =>
    api.patch(`/admin/content/blocks/${blockId}/draft`, { content, if_unmodified_since: ifUnmodifiedSince }),
  publishBlock: (blockId, ifUnmodifiedSince) =>
    api.post(`/admin/content/blocks/${blockId}/publish`, {}, { params: ifUnmodifiedSince ? { if_unmodified_since: ifUnmodifiedSince } : {} }),
  discardDraft: (blockId) => api.post(`/admin/content/blocks/${blockId}/discard_draft`),
  restoreFromHistory: (blockId, historyId) =>
    api.post(`/admin/content/blocks/${blockId}/restore`, { history_id: historyId }),
  setVisibility: (blockId, isVisible) =>
    api.patch(`/admin/content/blocks/${blockId}/visibility`, { is_visible: !!isVisible }),
  updateSEO: (pageKey, payload) => api.patch(`/admin/content/pages/${pageKey}/seo`, payload),
  updateUiString: (stringKey, value) => api.patch(`/admin/content/ui-strings/${encodeURIComponent(stringKey)}`, { value }),
  issuePreviewToken: (blockIds) => api.post("/admin/content/preview-token", { block_ids: blockIds }),
};

const USER_CACHE_KEY = "ccn_user_cache";

export const userCache = {
  set(user) {
    try {
      if (user) sessionStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
      else sessionStorage.removeItem(USER_CACHE_KEY);
    } catch (err) { /* noop */ }
  },
  get() {
    try {
      const raw = sessionStorage.getItem(USER_CACHE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (err) { return null; }
  },
  clear() {
    try { sessionStorage.removeItem(USER_CACHE_KEY); } catch (err) { /* noop */ }
  },
};
